import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { CustomersSection } from '../components/ui/customers-section';
import { LEGAL_ES } from '../constants/legalContent';
import { trackViewContent, trackInitiateCheckout } from '../lib/metaPixel';
import { motion, AnimatePresence } from 'motion/react';

/* ─── ICONS ─────────────────────────────────────────────── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7"/>
  </svg>
);
const IconChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5l7 7-7 7"/>
  </svg>
);
const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
  </svg>
);
const IconTruck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="6" width="14" height="11" rx="1"/><path d="M15 9h4l3 3v5h-7z"/><circle cx="6" cy="19" r="1.6"/><circle cx="17.5" cy="19" r="1.6"/>
  </svg>
);
const IconBolt = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z"/>
  </svg>
);
const IconShield = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 4 5v6c0 5 3.4 8.4 8 10 4.6-1.6 8-5 8-10V5z"/><path d="M9 12l2 2 4-4"/>
  </svg>
);
const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/>
  </svg>
);
const IconChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);

// Gallery slide: "next" enters from the right and exits to the left (and
// the reverse for "prev"), replacing what used to be an instant src swap —
// that hard cut was the "seco y tosco" feel this fixes.
const gallerySlideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 36 : -36, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -36 : 36, opacity: 0 }),
};

/* ─── HOW IT WORKS + FAQ CONTENT ─────────────────────────── */
const HOW_IT_WORKS_STEPS = [
  { title: "Compra tu tarjeta", text: "Elige la cantidad y paga de forma segura con MercadoPago." },
  { title: "Personaliza tu diseño", text: "Nos cuentas cómo la quieres y te la dejamos lista con tu marca." },
  { title: "Actívala y comparte", text: "Te llega a tu domicilio ya activada, lista para acercar a un celular." },
];

// Mismos logos de "Confían en nosotros" del Homepage (ver logos en
// Homepage.js), duplicados aquí en tamaño reducido para /comprar.
const TRUSTED_LOGOS = [
  { name: 'BICE', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782524053/contact/landing/tywy08ejjuslqb7nlqan.png' },
  { name: 'S4E', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534411/contact/landing/xbp3rfyo09fcgqnguctt.png' },
  { name: 'DXC', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788921433/DXC-Veritcal-Tagline-Full-Color-Dark_j89hje.png' },
  { name: 'UAI', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782504927/contact/landing/e6tylizrlwustq5zeydd.png' },
  { name: 'LIVUP', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1788835334/default-social-share-c-Photoroom_d7uuif.png' },
  { name: 'Vambe', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534632/contact/landing/jvxdzazc3verc6raewgl.png' },
  { name: '', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534316/contact/landing/yjm1j1qo41cco55lgxvg.png' },
  { name: 'Regulatek', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782534470/contact/landing/uamro4aql49lhqwmakaq.png' },
  { name: 'Tasky', image_url: 'https://res.cloudinary.com/dfyzvch4i/image/upload/f_auto,q_auto/v1782504507/contact/landing/wia3gjwx5ro48uiyynfe.png' },
];

const FAQ_ITEMS = [
  { q: "¿Hacen envíos a regiones?", a: "Sí, despachamos a todo Chile. El envío es gratis comprando 2 o más unidades; para 1 unidad el costo depende de tu comuna." },
  { q: "¿Es compatible con mi celular?", a: "Sí, funciona con NFC en iPhone y Android modernos, y además incluye un código QR como respaldo si el celular de la otra persona no tiene NFC." },
  { q: "¿Qué pasa si pierdo mi tarjeta?", a: "Puedes desactivarla y reemplazarla desde tu panel. Tu perfil y tus datos no se pierden, viven en tu cuenta, no en la tarjeta física." },
  { q: "¿Tiene garantía?", a: "Sí, si llega con un defecto de fabricación la reponemos sin costo. Escríbenos desde la misma página de tu pedido." },
];

/* ─── COMPRAR PAGE ───────────────────────────────────────── */
const ComprarPage = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [discountCode, setDiscountCode] = useState('');
  const [validatedDiscount, setValidatedDiscount] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Which way the gallery just moved — drives the slide direction in
  // goToImage below, so "next" always slides in from the right and "prev"
  // from the left, instead of the old hard cut.
  const [galleryDirection, setGalleryDirection] = useState(1);
  const goToImage = (index, dir) => {
    setGalleryDirection(dir ?? (index > currentImageIndex ? 1 : -1));
    setCurrentImageIndex(index);
  };
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDataProcessingModal, setShowDataProcessingModal] = useState(false);

  const [quoteFormData, setQuoteFormData] = useState({
    company: '', contact_person: '', email: '', phone: '', estimated_cards: '', comments: ''
  });
  const [quotationSent, setQuotationSent] = useState(false);
  const [isSubmittingQuotation, setIsSubmittingQuotation] = useState(false);
  const [quantityType, setQuantityType] = useState('preset');
  const [customQuantity, setCustomQuantity] = useState(1);
  const [quantityError, setQuantityError] = useState('');
  const [priceRanges, setPriceRanges] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const [formData, setFormData] = useState({
    customer_email: '', customer_name: '', customer_lastname: '', customer_phone: '',
    customer_rut: '', shipping_street: '', shipping_number: '', shipping_apartment: '',
    shipping_comuna: '', shipping_region: '', shipping_postal_code: '',
    receive_offers: false, save_info: false
  });

  const navigate = useNavigate();
  const touchStartXRef = useRef(null);
  const ctaStackRef = useRef(null);
  const [showStep1StickyCta, setShowStep1StickyCta] = useState(false);

  // The sticky mobile buy bar should only appear once the inline "Comprar
  // ahora" button has scrolled out of view — showing both at once (as it
  // did unconditionally) reads as a redundant, duplicated CTA, which isn't
  // how Shopify/Apple-style product pages do it. On desktop this observer
  // still runs but the bar stays CSS-hidden regardless, so it's harmless.
  useEffect(() => {
    if (step !== 1 || !ctaStackRef.current) return;
    const el = ctaStackRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStep1StickyCta(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [step]);

  // Swipe-to-navigate for the mobile gallery — the existing prev/next arrows
  // only reveal on `:hover`, which never fires on a touch device, so without
  // this the gallery is effectively unnavigable on mobile when there's more
  // than one media item. Desktop keeps using the hover arrows; this just
  // adds a second, touch-only way to change `currentImageIndex`.
  const handleGalleryTouchStart = (e) => { touchStartXRef.current = e.touches[0].clientX; };
  const handleGalleryTouchEnd = (e, count) => {
    if (touchStartXRef.current === null || count <= 1) return;
    const delta = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) { setGalleryDirection(1); setCurrentImageIndex(p => p === count - 1 ? 0 : p + 1); }
    else { setGalleryDirection(-1); setCurrentImageIndex(p => p === 0 ? count - 1 : p - 1); }
  };

  const chileanRegions = [
    "Región Metropolitana","Región de Arica y Parinacota","Región de Tarapacá",
    "Región de Antofagasta","Región de Atacama","Región de Coquimbo","Región de Valparaíso",
    "Región del Libertador General Bernardo O'Higgins","Región del Maule","Región de Ñuble",
    "Región del Biobío","Región de La Araucanía","Región de Los Ríos","Región de Los Lagos",
    "Región de Aysén del General Carlos Ibáñez del Campo",
    "Región de Magallanes y de la Antártica Chilena"
  ];

  useEffect(() => {
    loadProduct();
    axios.get(`${API}/public/quotations/config`).then(res => {
      if (res.data?.price_table) setPriceRanges(res.data.price_table);
    }).catch(() => {});
  }, []);

  // ViewContent fires once the real product loaded (cache hit or fresh
  // fetch both end here via setProduct), not on every render. Isolated in
  // its own try/catch — a tracking failure here must never be able to
  // affect the purchase flow below.
  useEffect(() => {
    if (product) {
      try {
        trackViewContent({ contentId: product.id, contentName: product.name, value: product.price, currency: 'CLP' });
      } catch (pixelError) {
        console.error('[MetaPixel] ViewContent tracking failed:', pixelError);
      }
    }
  }, [product]);

  const loadProduct = async () => {
    try {
      // No session cache here: this is the one page where the price must
      // always reflect the admin's latest edit, not whatever was fetched
      // up to 5 minutes ago in this tab (a promo/discount change needs to
      // show up immediately, not after a wait or a fresh tab).
      const response = await axios.get(`${API}/public/product/active`);
      setProduct(response.data);
    } catch (error) { console.error('Error loading product:', error); }
    finally { setLoading(false); }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    try {
      const response = await axios.post(`${API}/public/product/validate-discount`, { code: discountCode });
      if (response.data.valid) setValidatedDiscount(response.data.discount_percentage);
      else { setValidatedDiscount(null); alert('Código de descuento inválido'); }
    } catch (error) {
      console.error('Error validating discount code:', error);
      alert('No se pudo validar el código. Por favor intenta nuevamente.');
    }
  };

  const calculatePrices = () => {
    if (!product) return { unitPrice: 0, originalPrice: 0, subtotal: 0, shippingCost: 0, total: 0, hasDiscount: false, badgePercent: 0 };
    const originalPrice = Math.round(product.price);
    let unitPrice = originalPrice;
    let appliedDiscount = 0;
    // A validated discount code always wins. With no code, an exact promo
    // price (admin panel) takes precedence over the global discount
    // percentage — same precedence the backend's initiate-purchase uses, so
    // this never shows a price MercadoPago wouldn't actually charge.
    if (validatedDiscount) {
      appliedDiscount = validatedDiscount;
      unitPrice = Math.round(originalPrice * (1 - appliedDiscount / 100));
    } else if (product.promo_price > 0 && product.promo_price < originalPrice) {
      unitPrice = Math.round(product.promo_price);
    } else if (product.discount_percentage > 0) {
      appliedDiscount = product.discount_percentage;
      unitPrice = Math.round(originalPrice * (1 - appliedDiscount / 100));
    }
    const hasDiscount = unitPrice < originalPrice;
    const badgePercent = hasDiscount ? Math.round((1 - unitPrice / originalPrice) * 100) : 0;
    const subtotal = unitPrice * quantity;
    let shippingCost = 0;
    if (quantity >= 2) shippingCost = 0;
    else if (formData.shipping_region === "Región Metropolitana") shippingCost = 3500;
    return { unitPrice, originalPrice, subtotal, shippingCost, total: subtotal + shippingCost, appliedDiscount, hasDiscount, badgePercent };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const formatRUT = (value) => {
    const clean = value.replace(/[^0-9kK]/g, '');
    if (clean.length <= 1) return clean;
    const number = clean.slice(0, -1);
    const verifier = clean.slice(-1).toUpperCase();
    return `${number.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${verifier}`;
  };

  const handleRUTChange = (e) => setFormData(prev => ({ ...prev, customer_rut: formatRUT(e.target.value) }));

  const validateCustomQuantity = (value) => {
    if (!value || value === '') return 'Por favor ingresa una cantidad';
    if (value < 1) return 'La cantidad mínima es 1';
    if (value > 50) return 'Para más de 50 unidades, contáctanos directamente';
    return null;
  };

  const handleCustomQuantityChange = (value) => {
    setCustomQuantity(value);
    const error = validateCustomQuantity(value);
    setQuantityError(error || '');
    if (!error) setQuoteFormData({ ...quoteFormData, estimated_cards: value.toString() });
  };

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingQuotation) return;
    if (quantityType === 'custom') {
      const error = validateCustomQuantity(customQuantity);
      if (error) { setQuantityError(error); return; }
    }
    setIsSubmittingQuotation(true);
    try {
      await axios.post(`${API}/quotations`, { ...quoteFormData, origen: 'producto-page' });
      setQuotationSent(true);
      setQuoteFormData({ company: '', contact_person: '', email: '', phone: '', estimated_cards: '', comments: '' });
      setQuantityType('preset'); setCustomQuantity(1); setQuantityError('');
      setTimeout(() => { setShowQuoteModal(false); setQuotationSent(false); }, 3000);
    } catch (error) { console.error('Error sending quotation:', error); }
    finally { setIsSubmittingQuotation(false); }
  };

  const validateForm = () => {
    const required = ['customer_email','customer_name','customer_lastname','customer_phone',
      'customer_rut','shipping_street','shipping_number','shipping_comuna','shipping_region'];
    for (const field of required) {
      if (!formData[field]?.trim()) { alert(`Por favor completa: ${field.replace(/customer_|shipping_/g, '')}`); return false; }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) { alert('Correo electrónico inválido'); return false; }
    if (formData.customer_rut.length < 9) { alert('RUT inválido'); return false; }
    return true;
  };

  const handlePurchase = async () => {
    if (!validateForm()) return;
    setProcessingPayment(true);
    try {
      const payload = { ...formData, product_id: product.id, quantity, discount_code: validatedDiscount ? discountCode : null };
      console.log('[DEBUG] Payload a /payments/initiate-purchase (sin RUT):', { ...payload, customer_rut: '***' });
      const response = await axios.post(`${API}/payments/initiate-purchase`, payload);
      console.log('[DEBUG] Respuesta backend:', response.status, response.data);
      if (response.data.success) {
        // Fired only once MercadoPago has actually accepted the checkout
        // request and returned a real init_point to redirect to — not on
        // button click, so a rejected/failed initiate-purchase never counts
        // as a started checkout. Isolated in its own try/catch: the payment
        // session already exists at this point (the backend just confirmed
        // it above), so ANY tracking failure here — undefined function,
        // pixel blocked, whatever — must never stop the redirect that
        // actually sends the user to pay. A prior incident let exactly this
        // happen: a tracking ReferenceError bubbled into the outer catch
        // and the user never reached MercadoPago despite the order already
        // existing on our side.
        try {
          trackInitiateCheckout({ contentId: product.id, value: prices.total, currency: 'CLP', numItems: quantity });
        } catch (pixelError) {
          console.error('[MetaPixel] InitiateCheckout tracking failed (checkout continues):', pixelError);
        }
        // fbq() hands the event to an async network request; reassigning
        // location.href in the very same tick can tear down the page (and
        // cancel that in-flight request) before it ever reaches Meta's
        // servers, even though the call itself succeeded with no error.
        // This short, imperceptible delay is the standard mitigation —
        // gives the beacon a moment to actually leave the browser first.
        setTimeout(() => {
          window.location.href = response.data.init_point;
        }, 250);
      }
    } catch (error) {
      console.error('Error al iniciar compra:', error);
      alert('Error al procesar el pago. Por favor intenta nuevamente.');
      setProcessingPayment(false);
    }
  };

  const prices = product ? calculatePrices() : { unitPrice: 0, subtotal: 0, shippingCost: 0, total: 0 };
  const benefits = product?.benefits || [
    "Diseño visual personalizado con tu marca",
    "Compatible con iPhone y Android (NFC y QR)",
    "Panel de analíticas y métricas de interacción",
    "Alternativa ecológica a las tarjetas de papel",
    "Optimiza tu networking profesional",
    "Ideal para equipos comerciales y ferias"
  ];
  const mediaList = product?.media_gallery || [];

  /* ── LOADING ── */
  if (loading) return (
    <div className="cp-root cp-center-screen">
      <style>{CSS}</style>
      <div className="cp-spinner"/>
      <p className="cp-loading-txt">Cargando producto…</p>
    </div>
  );

  /* ── NO PRODUCT ── */
  if (!product) return (
    <div className="cp-root cp-center-screen">
      <style>{CSS}</style>
      <p className="cp-empty-txt">Producto no disponible</p>
      <button onClick={() => navigate('/')} className="cp-btn-ghost" style={{marginTop:16}}>
        <IconArrowLeft/> Volver al inicio
      </button>
    </div>
  );

  return (
    <div className="cp-root">

      {/* ══════════════════════════════════════════════════
          STEP 1 — PRODUCT VIEW
      ══════════════════════════════════════════════════ */}
      {step === 1 && (
        <>
          {/* NAV */}
          <header className="cp-nav">
            <div className="cp-nav-inner">
              <ContactLogo clickable />
              <button onClick={() => navigate('/')} className="cp-btn-back">
                <IconArrowLeft/> Volver
              </button>
            </div>
          </header>

          {/* ── TWO-COLUMN PRODUCT LAYOUT ── */}
          <div className="cp-product-layout">

            {/* LEFT — Gallery (sticky) */}
            <div className="cp-gallery-col">
              <div className="cp-gallery-main"
                onTouchStart={handleGalleryTouchStart}
                onTouchEnd={(e) => handleGalleryTouchEnd(e, mediaList.length)}>
                {/* Padding lives on this wrapper div, not on the <img>/<video>
                    itself — object-fit + padding directly on a replaced
                    element is inconsistently honored across mobile browser
                    engines (some ignore the padding for sizing purposes,
                    which combined with the parent's overflow:hidden made
                    real product photos render larger than their frame and
                    get visibly clipped). A plain div's padding has no such
                    ambiguity. */}
                <div className="cp-gallery-media-frame">
                  {mediaList.length > 0 ? (
                    <AnimatePresence initial={false} custom={galleryDirection} mode="popLayout">
                      <motion.div
                        key={currentImageIndex}
                        custom={galleryDirection}
                        variants={gallerySlideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        className="cp-gallery-slide"
                      >
                        {mediaList[currentImageIndex].type === 'image' ? (
                          <img src={mediaList[currentImageIndex].url} alt={`${product.name} — ${currentImageIndex + 1}`} className="cp-gallery-img"/>
                        ) : (
                          <video src={mediaList[currentImageIndex].url} controls autoPlay loop muted className="cp-gallery-img"/>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  ) : product.image_url ? (
                    <img src={`${API}/files/${product.image_url}`} alt={product.name} className="cp-gallery-img"/>
                  ) : (
                    <img src="/images/video-homepage.png" alt={product.name} className="cp-gallery-img"/>
                  )}
                </div>
                {mediaList.length > 1 && (
                  <>
                    <button className="cp-gallery-arrow cp-gallery-arrow--left"
                      onClick={() => { setGalleryDirection(-1); setCurrentImageIndex(p => p === 0 ? mediaList.length - 1 : p - 1); }} aria-label="Anterior">
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
                    </button>
                    <button className="cp-gallery-arrow cp-gallery-arrow--right"
                      onClick={() => { setGalleryDirection(1); setCurrentImageIndex(p => p === mediaList.length - 1 ? 0 : p + 1); }} aria-label="Siguiente">
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                    </button>
                    <div className="cp-gallery-counter">{currentImageIndex + 1} / {mediaList.length}</div>
                  </>
                )}
              </div>
              {mediaList.length > 1 && (
                <div className="cp-thumbs">
                  {mediaList.map((media, idx) => (
                    <button key={media.id || idx} onClick={() => goToImage(idx)}
                      className={`cp-thumb ${currentImageIndex === idx ? 'cp-thumb--active' : ''}`} aria-label={`Vista ${idx + 1}`}>
                      {media.type === 'image' ? (
                        <img src={media.url} alt={`Thumb ${idx + 1}`} className="cp-thumb-img"/>
                      ) : (
                        <div className="cp-thumb-video">
                          <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {/* Mobile-only: the thumbnail strip above is hidden on touch
                  screens (targets are small and easy to mis-tap) in favor
                  of swipe + these dots — the standard mobile gallery pattern
                  on Shopify/Apple-style product pages. */}
              {mediaList.length > 1 && (
                <div className="cp-gallery-dots">
                  {mediaList.map((_, idx) => (
                    <button key={idx} onClick={() => goToImage(idx)}
                      className={`cp-gallery-dot ${currentImageIndex === idx ? 'cp-gallery-dot--active' : ''}`}
                      aria-label={`Ir a imagen ${idx + 1}`}/>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT — Product details */}
            <div className="cp-details-col">

              {/* Title */}
              <div className="cp-product-head">
                <h1 className="cp-product-title">{product.name}</h1>
                <p className="cp-product-subtitle">
                  {product.subtitle || "La herramienta definitiva para equipos comerciales modernos"}
                </p>
              </div>

              {/* Price */}
              <div className="cp-price-section">
                <div className="cp-price-row">
                  {prices.hasDiscount && (
                    <span className="cp-price-original">${prices.originalPrice?.toLocaleString('es-CL')}</span>
                  )}
                  <span className="cp-price-main">${prices.unitPrice?.toLocaleString('es-CL')}</span>
                  <span className="cp-price-currency">CLP</span>
                  {prices.hasDiscount && (
                    <span className="cp-discount-badge">{prices.badgePercent}% OFF</span>
                  )}
                </div>
                <div className="cp-price-note">Precio por unidad</div>
              </div>

              {/* CTAs */}
              <div className="cp-cta-stack" ref={ctaStackRef}>
                <button onClick={() => setStep(2)} className="cp-btn-primary">
                  Comprar ahora <IconArrowRight/>
                </button>
                <a
                  href={`https://wa.me/56982036712?text=${encodeURIComponent('Hola, estoy interesado en comprar una tarjeta NFC de Contact.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-btn-outline cp-btn-whatsapp"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Comprar por WhatsApp
                </a>
                <button onClick={() => navigate('/register')} className="cp-btn-outline">
                  Crear perfil digital gratis
                </button>
                <button onClick={() => setShowQuoteModal(true)} className="cp-link-secondary">
                  Cotización corporativa (3+ unidades)
                </button>
              </div>

              {/* Trust bar — the three things most likely to stop someone
                  from buying a product they don't know well yet (shipping
                  cost, how fast it's usable, payment safety), answered in
                  one glance right under the button. Standard ecommerce
                  pattern (Amazon/Shopify-style), kept to icons + short
                  labels so it doesn't compete with the CTA above it. */}
              <div className="cp-trust-bar">
                <div className="cp-trust-item"><IconTruck/> Envío gratis desde 2 unidades</div>
                <div className="cp-trust-item"><IconClock/> Recibe en los próximos 5 días</div>
                <div className="cp-trust-item"><IconBolt/> Activación inmediata</div>
                <div className="cp-trust-item"><IconShield/> Pago seguro MercadoPago</div>
              </div>

              {/* Benefits */}
              <div className="cp-benefits-section">
                <div className="cp-benefits-label">Características</div>
                <div className="cp-benefits-list">
                  {benefits.slice(0, 4).map((b, i) => (
                    <div key={i} className="cp-benefit-row">
                      <span className="cp-benefit-ico"><IconCheck/></span>
                      <span className="cp-benefit-txt">{b}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setShowBenefitsModal(true)} className="cp-link-btn">
                  Ver todos los beneficios <IconChevronRight/>
                </button>
              </div>

              {/* Payment info */}
              <p className="cp-payment-note"><IconLock/> Pago 100% seguro con MercadoPago.</p>

            </div>
          </div>

          {/* ── TRUSTED BY ── same logos as the Homepage's "Confían en
              nosotros" section, reused here at a smaller size as social
              proof right before the purchase flow explains itself. */}
          <div className="cp-trusted-section">
            <CustomersSection
              label="Confían en nosotros"
              customers={TRUSTED_LOGOS.map(l => ({ src: l.image_url, alt: l.name, height: 26 }))}
            />
          </div>

          {/* ── HOW IT WORKS ── 3-step explainer. This isn't a familiar
              product the way clothing or electronics are, so unlike a
              typical PDP, showing the actual flow (buy → design → arrives
              activated) does real work: it answers "how does this even
              work" before that doubt turns into an abandoned page. */}
          <div className="cp-howitworks-section">
            <h2 className="cp-section-title">Cómo funciona</h2>
            <div className="cp-howitworks-grid">
              {HOW_IT_WORKS_STEPS.map((s, i) => (
                <div key={i} className="cp-howitworks-step">
                  <div className="cp-howitworks-num">{i + 1}</div>
                  <h3 className="cp-howitworks-step-title">{s.title}</h3>
                  <p className="cp-howitworks-step-txt">{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── FAQ ── Objection-handling, not a generic help section — the
              4 questions here are specifically the ones that stall a
              purchase of a product people haven't bought before (shipping,
              compatibility, loss, warranty), answered inline instead of
              making someone go find a support page mid-checkout. */}
          <div className="cp-faq-section">
            <h2 className="cp-section-title">Preguntas frecuentes</h2>
            <div className="cp-faq-list">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className={`cp-faq-item ${openFaqIndex === i ? 'cp-faq-item--open' : ''}`}>
                  <button className="cp-faq-question" onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}>
                    {item.q}
                    <span className="cp-faq-chevron"><IconChevronDown/></span>
                  </button>
                  {openFaqIndex === i && <p className="cp-faq-answer">{item.a}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* ── FINAL CTA ── For whoever scrolled through everything above
              and only now feels ready — the first "Comprar ahora" is long
              off-screen by this point. Standard close for a product page
              that's making its case in sections rather than a single
              above-the-fold pitch. */}
          <div className="cp-final-cta-section">
            <h2 className="cp-final-cta-title">¿List@ para tener tu tarjeta?</h2>
            <button onClick={() => setStep(2)} className="cp-btn-primary cp-final-cta-btn">
              Comprar ahora <IconArrowRight/>
            </button>
          </div>

          {/* Reserves room for the fixed sticky CTA bar below so it never
              overlaps the last real content (the product layout). */}
          <div className="cp-step1-bottom-spacer"/>

          {/* Mobile-only sticky CTA — on step 1, "Comprar ahora" sits
              inline near the top of the page, so on a long mobile scroll
              it's long gone by the time
              someone's ready to buy. Mirrors the step-2 sticky bar with
              the same price+button pattern big ecommerce/SaaS product
              pages use, plus a small trust line since this is the first
              moment a mobile visitor is asked to commit to buying. */}
          <div className={`cp-step1-mobile-cta ${showStep1StickyCta ? 'cp-step1-mobile-cta--visible' : ''}`}>
            <div className="cp-step1-mobile-cta-top">
              <div className="cp-mobile-stickybar-price">
                <span className="cp-mobile-stickybar-label">Precio unitario</span>
                <span className="cp-mobile-stickybar-amount">${prices.unitPrice?.toLocaleString('es-CL')} <small>CLP</small></span>
              </div>
              <button onClick={() => setStep(2)} className="cp-btn-primary cp-step1-mobile-cta-btn">
                Comprar ahora <IconArrowRight/>
              </button>
            </div>
            <div className="cp-step1-mobile-trust"><IconLock/> Pago 100% seguro con MercadoPago</div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════
          STEP 2 — CHECKOUT
      ══════════════════════════════════════════════════ */}
      {step === 2 && (
        <>
          <header className="cp-nav cp-nav--scrolled">
            <div className="cp-nav-inner">
              <div style={{display:'flex',alignItems:'center',gap:16}}>
                <button onClick={() => setStep(1)} className="cp-btn-back">
                  <IconArrowLeft/> Volver
                </button>
                <div className="cp-nav-divider"/>
                <ContactLogo clickable/>
              </div>
              <div className="cp-step-indicator">
                <span className="cp-step cp-step--done">1</span>
                <div className="cp-step-line"/>
                <span className="cp-step cp-step--active">2</span>
                <div className="cp-step-line"/>
                <span className="cp-step-label">Pago</span>
              </div>
            </div>
          </header>

          {/* Mobile-only: the desktop step indicator (1 ● — ● 2) is hidden
              on small screens (no room for it in the header); this gives
              mobile its own equivalent — a compact progress bar + a mini
              order recap right up top, so someone who just landed on
              checkout sees immediately what they're buying and how far
              along they are, before scrolling into a long address form.
              Read-only — all functional controls (discount code, terms,
              pay) stay exactly where they already were, below. */}
          <div className="cp-mobile-progress-wrap">
            <div className="cp-mobile-progress-row">
              <span className="cp-mobile-progress-label">Paso 2 de 2</span>
              <span className="cp-mobile-progress-label">Datos de envío y pago</span>
            </div>
            <div className="cp-mobile-progress-bar"><div className="cp-mobile-progress-fill"/></div>
            <div className="cp-mobile-order-recap">
              <div className="cp-mobile-order-recap-info">
                <span className="cp-mobile-order-recap-name">{product.name}</span>
                <span className="cp-mobile-order-recap-qty">{quantity} unidad{quantity > 1 ? 'es' : ''}</span>
              </div>
              <span className="cp-mobile-order-recap-total">${prices.total?.toLocaleString('es-CL')}</span>
            </div>
          </div>

          <div className="cp-checkout-wrap">
            <div className="cp-checkout-grid">

              {/* ── LEFT: Form ── */}
              <div className="cp-checkout-left">
                <div style={{marginBottom:40}}>
                  <h2 className="cp-checkout-title">Finalizar compra</h2>
                  <p className="cp-checkout-sub">Completa tus datos para procesar el pedido</p>
                </div>

                <div className="cp-form-section">
                  <h3 className="cp-form-section-title">Cantidad</h3>
                  <div className="cp-qty-row">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1} className="cp-qty-btn" aria-label="Reducir">
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4"/></svg>
                    </button>
                    <div className="cp-qty-display">{quantity}</div>
                    <button onClick={() => setQuantity(quantity + 1)} className="cp-qty-btn" aria-label="Aumentar">
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                    </button>
                    {quantity >= 2 && (
                      <span className="cp-free-shipping"><IconCheck/> Envío gratis incluido</span>
                    )}
                  </div>
                  <p className="cp-qty-note">
                    ¿Necesitas más de 10 unidades?{' '}
                    <button onClick={() => navigate('/cotizar')} className="cp-link-inline">Ver precios corporativos</button>
                  </p>
                </div>

                <div className="cp-form-section">
                  <h3 className="cp-form-section-title">Contacto</h3>
                  <div className="cp-field">
                    <label className="cp-label" htmlFor="customer_email">Correo electrónico <span className="cp-req">*</span></label>
                    <input id="customer_email" type="email" name="customer_email" value={formData.customer_email}
                      onChange={handleInputChange} required placeholder="tu@email.com" className="cp-input" autoComplete="email"/>
                  </div>
                  <label className="cp-checkbox-row">
                    <input type="checkbox" name="receive_offers" checked={formData.receive_offers} onChange={handleInputChange} className="cp-checkbox"/>
                    <span className="cp-checkbox-label">Recibir novedades y ofertas por correo electrónico</span>
                  </label>
                </div>

                <div className="cp-form-section">
                  <h3 className="cp-form-section-title">Entrega</h3>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="customer_name">Nombre <span className="cp-req">*</span></label>
                      <input id="customer_name" type="text" name="customer_name" value={formData.customer_name}
                        onChange={handleInputChange} required className="cp-input" autoComplete="given-name"/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="customer_lastname">Apellidos <span className="cp-req">*</span></label>
                      <input id="customer_lastname" type="text" name="customer_lastname" value={formData.customer_lastname}
                        onChange={handleInputChange} required className="cp-input" autoComplete="family-name"/>
                    </div>
                  </div>
                  <div className="cp-field-row cp-field-row--3-1">
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="shipping_street">Calle <span className="cp-req">*</span></label>
                      <input id="shipping_street" type="text" name="shipping_street" value={formData.shipping_street}
                        onChange={handleInputChange} required placeholder="Av. Providencia" className="cp-input" autoComplete="street-address"/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="shipping_number">Número <span className="cp-req">*</span></label>
                      <input id="shipping_number" type="text" name="shipping_number" value={formData.shipping_number}
                        onChange={handleInputChange} required placeholder="1234" className="cp-input"/>
                    </div>
                  </div>
                  <div className="cp-field">
                    <label className="cp-label" htmlFor="shipping_apartment">Departamento / Casa (opcional)</label>
                    <input id="shipping_apartment" type="text" name="shipping_apartment" value={formData.shipping_apartment}
                      onChange={handleInputChange} placeholder="Depto 304" className="cp-input" autoComplete="address-line2"/>
                  </div>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="customer_rut">RUT <span className="cp-req">*</span></label>
                      <input id="customer_rut" type="text" name="customer_rut" value={formData.customer_rut}
                        onChange={handleRUTChange} required placeholder="12.345.678-9" className="cp-input" maxLength={12}/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="shipping_postal_code">Código postal (opcional)</label>
                      <input id="shipping_postal_code" type="text" name="shipping_postal_code" value={formData.shipping_postal_code}
                        onChange={handleInputChange} placeholder="7500000" className="cp-input" autoComplete="postal-code"/>
                    </div>
                  </div>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="shipping_comuna">Comuna <span className="cp-req">*</span></label>
                      <input id="shipping_comuna" type="text" name="shipping_comuna" value={formData.shipping_comuna}
                        onChange={handleInputChange} required placeholder="Santiago" className="cp-input" autoComplete="address-level2"/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label" htmlFor="shipping_region">Región <span className="cp-req">*</span></label>
                      <select id="shipping_region" name="shipping_region" value={formData.shipping_region}
                        onChange={handleInputChange} required className="cp-input cp-select" autoComplete="address-level1">
                        <option value="">Selecciona región</option>
                        {chileanRegions.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="cp-field">
                    <label className="cp-label" htmlFor="customer_phone">Teléfono <span className="cp-req">*</span></label>
                    <input id="customer_phone" type="tel" name="customer_phone" value={formData.customer_phone}
                      onChange={handleInputChange} required placeholder="+56 9 1234 5678" className="cp-input" autoComplete="tel"/>
                  </div>
                  <label className="cp-checkbox-row" style={{marginTop:4}}>
                    <input type="checkbox" name="save_info" checked={formData.save_info} onChange={handleInputChange} className="cp-checkbox"/>
                    <span className="cp-checkbox-label">Guardar mi información para futuras compras</span>
                  </label>
                </div>
              </div>

              {/* ── RIGHT: Order Summary ── */}
              <div className="cp-checkout-right">
                <div className="cp-summary-card">
                  <h3 className="cp-summary-title">Resumen del pedido</h3>
                  <div className="cp-summary-rows">
                    <div className="cp-summary-row"><span>Producto</span><span className="cp-summary-val">{product.name}</span></div>
                    <div className="cp-summary-row"><span>Cantidad</span><span className="cp-summary-val">{quantity} unidad{quantity > 1 ? 'es' : ''}</span></div>
                    <div className="cp-summary-row"><span>Precio unitario</span><span className="cp-summary-val">${prices.unitPrice?.toLocaleString('es-CL')}</span></div>
                    {prices.hasDiscount && (
                      <div className="cp-summary-row cp-summary-row--accent">
                        <span>Descuento ({prices.badgePercent}%)</span>
                        <span>-${((prices.originalPrice - prices.unitPrice) * quantity).toLocaleString('es-CL')}</span>
                      </div>
                    )}
                    <div className="cp-summary-row cp-summary-row--border"><span>Subtotal</span><span className="cp-summary-val">${prices.subtotal?.toLocaleString('es-CL')}</span></div>
                    <div className="cp-summary-row">
                      <span>Envío</span>
                      <span className="cp-summary-val">
                        {quantity >= 2 ? <span className="cp-summary-accent-txt">GRATIS</span>
                          : formData.shipping_region === "Región Metropolitana" ? `$${prices.shippingCost?.toLocaleString('es-CL')}`
                          : formData.shipping_region ? <span className="cp-summary-warn-txt">Por pagar (Starken)</span>
                          : <span style={{color:'var(--cp-faint)'}}>Selecciona región</span>}
                      </span>
                    </div>
                    <div className="cp-summary-total"><span>Total</span><span>${prices.total?.toLocaleString('es-CL')} CLP</span></div>
                  </div>

                  <div className="cp-discount-section">
                    <label className="cp-label">Código de descuento</label>
                    <div className="cp-discount-row">
                      <input type="text" value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="CODIGO2026" disabled={!!validatedDiscount} className="cp-input" style={{flex:1}}/>
                      {!validatedDiscount ? (
                        <button onClick={validateDiscountCode} className="cp-btn-apply">Aplicar</button>
                      ) : (
                        <button onClick={() => { setDiscountCode(''); setValidatedDiscount(null); }} className="cp-btn-remove">Quitar</button>
                      )}
                    </div>
                    {validatedDiscount && (
                      <div className="cp-discount-ok"><IconCheck/> Descuento del {validatedDiscount}% aplicado</div>
                    )}
                  </div>

                  <label className="cp-terms-row">
                    <input type="checkbox" id="acceptTerms" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} className="cp-checkbox"/>
                    <span className="cp-checkbox-label">
                      He leído y acepto los{' '}
                      <button type="button" onClick={() => setShowTermsModal(true)} className="cp-link-inline">Términos y Condiciones</button>
                      {' '}y la{' '}
                      <button type="button" onClick={() => setShowPrivacyModal(true)} className="cp-link-inline">Política de Privacidad</button>
                      {' '}de Contact Network
                    </span>
                  </label>
                  <p style={{fontSize:12,color:'var(--cp-muted)',margin:'-4px 0 0 26px'}}>
                    Más información sobre cómo tratamos tus datos en nuestra{' '}
                    <button type="button" onClick={() => setShowDataProcessingModal(true)} className="cp-link-inline">Política de Tratamiento de Datos</button>
                  </p>

                  <button onClick={handlePurchase} disabled={processingPayment || !acceptedTerms}
                    className={`cp-btn-pay ${(!acceptedTerms || processingPayment) ? 'cp-btn-pay--disabled' : ''}`}>
                    {processingPayment ? 'Procesando pago…' : !acceptedTerms ? 'Acepta los términos para continuar' : 'Continuar al pago'}
                  </button>
                  {!acceptedTerms && <p className="cp-terms-warning">Debes aceptar los términos para continuar</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-only sticky bar: on desktop the summary+CTA are already
              sticky alongside the form (see .cp-checkout-right), but
              stacking to one column on mobile pushed that same block to
              the very end of a long scroll — this mirrors it at the
              bottom of the viewport so price + pay button stay reachable
              the whole time, the same pattern big ecommerce checkouts use
              on mobile. Reuses handlePurchase/acceptedTerms/processingPayment
              as-is — no new logic, just a second, always-visible entry
              point to the same action. */}
          <div className="cp-mobile-stickybar">
            <div className="cp-mobile-stickybar-price">
              <span className="cp-mobile-stickybar-label">Total</span>
              <span className="cp-mobile-stickybar-amount">${prices.total?.toLocaleString('es-CL')} <small>CLP</small></span>
            </div>
            <button onClick={handlePurchase} disabled={processingPayment || !acceptedTerms}
              className={`cp-btn-pay cp-mobile-stickybar-btn ${(!acceptedTerms || processingPayment) ? 'cp-btn-pay--disabled' : ''}`}>
              {processingPayment ? 'Procesando…' : !acceptedTerms ? 'Acepta los términos' : 'Pagar'}
            </button>
          </div>
        </>
      )}

      {/* ══ MODAL BENEFICIOS ══ */}
      {showBenefitsModal && (
        <div className="cp-modal-overlay" onClick={() => setShowBenefitsModal(false)}>
          <div className="cp-modal" onClick={e => e.stopPropagation()}>
            <div className="cp-modal-head">
              <div>
                <h2 className="cp-modal-h2">Beneficios y Características</h2>
                <p className="cp-modal-sub">Todo lo que obtienes con tu Tarjeta Inteligente CONTACT</p>
              </div>
              <button onClick={() => setShowBenefitsModal(false)} className="cp-modal-close" aria-label="Cerrar">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="cp-modal-body">
              <div className="cp-benefits-modal-list">
                {benefits.map((b, i) => (
                  <div key={i} className="cp-benefit-card">
                    <span className="cp-benefit-card-num">{String(i + 1).padStart(2, '0')}</span>
                    <p className="cp-benefit-card-txt">{b}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL COTIZACIÓN ══ */}
      {showQuoteModal && (
        <div className="cp-modal-overlay" onClick={() => setShowQuoteModal(false)}>
          <div className="cp-modal" onClick={e => e.stopPropagation()}>
            <div className="cp-modal-head">
              <div>
                <h2 className="cp-modal-h2">Cotización Corporativa</h2>
                <p className="cp-modal-sub">Te responderemos en menos de 24 horas hábiles.</p>
              </div>
              <button onClick={() => setShowQuoteModal(false)} className="cp-modal-close" aria-label="Cerrar">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="cp-modal-body">
              {quotationSent ? (
                <div className="cp-quote-success">
                  <div className="cp-success-ico"><IconCheck/></div>
                  <h3>Solicitud recibida</h3>
                  <p>Te contactaremos dentro de las próximas 24 horas hábiles con una propuesta personalizada.</p>
                </div>
              ) : (
                <form onSubmit={handleQuotationSubmit}>
                  <div className="cp-field">
                    <label className="cp-label">Empresa <span className="cp-req">*</span></label>
                    <input type="text" required value={quoteFormData.company}
                      onChange={e => setQuoteFormData({...quoteFormData, company: e.target.value})}
                      placeholder="ACME S.A." className="cp-input"/>
                  </div>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label className="cp-label">Persona de contacto <span className="cp-req">*</span></label>
                      <input type="text" required value={quoteFormData.contact_person}
                        onChange={e => setQuoteFormData({...quoteFormData, contact_person: e.target.value})}
                        placeholder="Ana Rodríguez" className="cp-input"/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label">Correo electrónico <span className="cp-req">*</span></label>
                      <input type="email" required value={quoteFormData.email}
                        onChange={e => setQuoteFormData({...quoteFormData, email: e.target.value})}
                        placeholder="ana@empresa.com" className="cp-input"/>
                    </div>
                  </div>
                  <div className="cp-field-row">
                    <div className="cp-field">
                      <label className="cp-label">Teléfono <span className="cp-req">*</span></label>
                      <input type="tel" required value={quoteFormData.phone}
                        onChange={e => setQuoteFormData({...quoteFormData, phone: e.target.value})}
                        placeholder="+56 9 1234 5678" className="cp-input"/>
                    </div>
                    <div className="cp-field">
                      <label className="cp-label">Cantidad estimada <span className="cp-req">*</span></label>
                      {quantityType === 'preset' ? (
                        <select required value={quoteFormData.estimated_cards}
                          onChange={e => setQuoteFormData({...quoteFormData, estimated_cards: e.target.value})}
                          className="cp-input cp-select">
                          <option value="">Selecciona un rango</option>
                          {priceRanges.map((r, i) => <option key={i} value={r.range}>{r.description}</option>)}
                        </select>
                      ) : (
                        <div className="cp-qty-row" style={{border:'1px solid var(--cp-border)',borderRadius:6,padding:'4px 8px'}}>
                          <button type="button" className="cp-qty-btn" style={{width:26,height:26}}
                            onClick={() => handleCustomQuantityChange(Math.max(1, customQuantity - 1))}>−</button>
                          <input type="number" min="1" max="50" value={customQuantity} className="cp-input"
                            style={{border:'none',textAlign:'center',flex:1,padding:'4px 0'}}
                            onChange={e => handleCustomQuantityChange(parseInt(e.target.value) || '')}/>
                          <button type="button" className="cp-qty-btn" style={{width:26,height:26}}
                            onClick={() => handleCustomQuantityChange(Math.min(50, customQuantity + 1))}>+</button>
                        </div>
                      )}
                      <div className="cp-qty-toggle-row">
                        <label className="cp-toggle-opt">
                          <input type="radio" value="preset" checked={quantityType==='preset'}
                            onChange={() => { setQuantityType('preset'); setQuantityError(''); setQuoteFormData({...quoteFormData, estimated_cards: ''}); }}/>
                          Rangos
                        </label>
                        <label className="cp-toggle-opt">
                          <input type="radio" value="custom" checked={quantityType==='custom'}
                            onChange={() => { setQuantityType('custom'); handleCustomQuantityChange(customQuantity); }}/>
                          Personalizado
                        </label>
                      </div>
                      {quantityError && <p className="cp-error">{quantityError}</p>}
                    </div>
                  </div>
                  <div className="cp-field">
                    <label className="cp-label">Descripción del proyecto</label>
                    <textarea rows="3" value={quoteFormData.comments}
                      onChange={e => setQuoteFormData({...quoteFormData, comments: e.target.value})}
                      placeholder="Cuéntanos sobre tu empresa y necesidades..." className="cp-input cp-textarea"/>
                  </div>
                  <button type="submit" disabled={isSubmittingQuotation} className="cp-btn-submit-quote">
                    {isSubmittingQuotation ? 'Enviando…' : 'Solicitar cotización'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ MODALES LEGALES ══ */}
      {/* Content comes from constants/legalContent.js — the single source of
          truth shared with Homepage.js and RegisterPage.js. */}
      {showTermsModal && (
        <div className="cp-modal-overlay" onClick={e => e.target === e.currentTarget && setShowTermsModal(false)}>
          <div className="cp-modal cp-modal--wide">
            <div className="cp-modal-head">
              <h2 className="cp-modal-h2">{LEGAL_ES.terms.title}</h2>
              <button onClick={() => setShowTermsModal(false)} className="cp-modal-close" aria-label="Cerrar">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="cp-modal-body cp-terms-body">
              <div style={{textAlign:'center',marginBottom:24}}>
                <strong style={{fontSize:15,color:'var(--cp-text)'}}>{LEGAL_ES.terms.heading}</strong>
              </div>
              {LEGAL_ES.terms.sections.map(([title, body], i) => (
                <section key={i} style={{marginBottom:20}}>
                  <h4 style={{fontSize:13,fontWeight:700,color:'var(--cp-text)',marginBottom:6}}>{title}</h4>
                  {body.split('\n\n').map((p, j) => <p key={j} style={{fontSize:13,color:'var(--cp-muted)',lineHeight:1.7,marginTop:j>0?8:0}}>{p}</p>)}
                </section>
              ))}
            </div>
            <div className="cp-modal-foot">
              <button onClick={() => { setAcceptedTerms(true); setShowTermsModal(false); }} className="cp-btn-primary" style={{width:'100%'}}>
                Acepto los Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      )}

      {[
        { show: showPrivacyModal, close: () => setShowPrivacyModal(false), data: LEGAL_ES.privacy },
        { show: showDataProcessingModal, close: () => setShowDataProcessingModal(false), data: LEGAL_ES.dataProcessing },
      ].map(({ show, close, data }) => show && (
        <div key={data.title} className="cp-modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
          <div className="cp-modal cp-modal--wide">
            <div className="cp-modal-head">
              <h2 className="cp-modal-h2">{data.title}</h2>
              <button onClick={close} className="cp-modal-close" aria-label="Cerrar">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="cp-modal-body cp-terms-body">
              <div style={{textAlign:'center',marginBottom:24}}>
                <strong style={{fontSize:15,color:'var(--cp-text)'}}>{data.heading}</strong>
              </div>
              {data.sections.map(([title, body], i) => (
                <section key={i} style={{marginBottom:20}}>
                  <h4 style={{fontSize:13,fontWeight:700,color:'var(--cp-text)',marginBottom:6}}>{title}</h4>
                  {body.split('\n\n').map((p, j) => <p key={j} style={{fontSize:13,color:'var(--cp-muted)',lineHeight:1.7,marginTop:j>0?8:0}}>{p}</p>)}
                </section>
              ))}
            </div>
            <div className="cp-modal-foot">
              <button onClick={close} className="cp-btn-primary" style={{width:'100%'}}>{data.done}</button>
            </div>
          </div>
        </div>
      ))}

      {/* Floating WhatsApp contact button — visible on both steps, mirrored
          to the opposite corner from the global ChatBot bubble (bottom-right,
          see components/ChatBot.js) so the two never overlap. Pre-fills the
          message with explicit buy intent since this is specifically the
          /comprar page, not a general support contact. */}
      <a
        href={`https://wa.me/56982036712?text=${encodeURIComponent('Hola, estoy interesado en comprar una tarjeta NFC de Contact.')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="cp-whatsapp-float"
        aria-label="Contactar por WhatsApp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────── */
const CSS = `
:root{
  --cp-bg:#FAFAF9;
  --cp-bg-w:#FFFFFF;
  --cp-text:#111111;
  --cp-muted:#6E6E73;
  --cp-faint:#AEAEB2;
  --cp-border:rgba(0,0,0,.08);
  --cp-border-dk:rgba(0,0,0,.18);
  --cp-dark:#111111;
  --cp-blue:#0071E3;
  --cp-bg-tint:#F2F5F8;
}

/* ── ROOT ── */
.cp-root{font-family:'Inter',system-ui,sans-serif;background:var(--cp-bg);color:var(--cp-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}

/* Floating WhatsApp button — stacked directly above the global ChatBot
   bubble (same corner, bottom-right) on this page specifically, instead of
   the opposite corner. Offsets below are chatbot's own bottom + its 50px
   height + a small gap, at each of chatbot's own breakpoints (default and
   its raised state on /comprar past max-width:480px — see ChatBot.js),
   so the stack holds together at every width. */
.cp-whatsapp-float{position:fixed;bottom:84px;right:24px;width:50px;height:50px;border-radius:50%;background:#25D366;display:flex;align-items:center;justify-content:center;z-index:50;box-shadow:0 10px 28px rgba(37,211,102,.35),0 2px 8px rgba(0,0,0,.14);transition:transform .15s,box-shadow .15s}
.cp-whatsapp-float:hover{transform:translateY(-2px);box-shadow:0 14px 34px rgba(37,211,102,.42),0 3px 10px rgba(0,0,0,.16)}
@media(max-width:480px){.cp-whatsapp-float{bottom:148px;right:16px}}
.cp-root::before{content:'';position:fixed;inset:0;z-index:0;pointer-events:none;background:radial-gradient(ellipse 1100px 700px at 50% -10%,rgba(0,113,227,.05),transparent 60%),radial-gradient(ellipse 900px 600px at 100% 100%,rgba(17,17,17,.035),transparent 55%)}
.cp-root::after{content:'';position:fixed;inset:0;z-index:0;pointer-events:none;opacity:.5;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90' viewBox='0 0 90 90'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E")}
.cp-nav,.cp-product-layout,.cp-checkout-wrap,.cp-modal-overlay{position:relative;z-index:1}

/* ── NAV ── */
.cp-nav{position:sticky;top:0;z-index:100;background:rgba(250,250,249,.85);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-bottom:1px solid transparent;transition:border-color .25s}
.cp-nav--scrolled{border-color:var(--cp-border)}
.cp-nav-inner{max-width:1280px;margin:0 auto;padding:0 48px;height:64px;display:flex;align-items:center;justify-content:space-between}
.cp-nav-divider{width:1px;height:18px;background:var(--cp-border)}
.cp-btn-back{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:var(--cp-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.cp-btn-back:hover{color:var(--cp-text)}

/* ── TWO-COLUMN PRODUCT LAYOUT ── */
.cp-product-layout{display:grid;grid-template-columns:1.15fr .85fr;gap:96px;max-width:1280px;margin:0 auto;padding:32px 48px 96px;align-items:start}
.cp-gallery-col{position:sticky;top:104px}
.cp-details-col{padding-top:24px}

/* ── PRODUCT HEAD ── */
.cp-product-head{margin-bottom:36px}
.cp-product-title{font-size:clamp(34px,4.4vw,52px);font-weight:300;line-height:1.05;letter-spacing:-.03em;color:var(--cp-text);margin-bottom:14px}
.cp-product-subtitle{font-size:16px;color:var(--cp-muted);line-height:1.6;max-width:34ch}

/* ── GALLERY ── */
.cp-gallery-main{position:relative;aspect-ratio:1;max-height:820px;background:#F3F3F1;border-radius:18px;overflow:hidden;width:100%;box-shadow:0 1px 2px rgba(0,0,0,.04),0 16px 40px rgba(0,0,0,.07)}
.cp-gallery-media-frame{position:relative;width:100%;height:100%;padding:8px;box-sizing:border-box}
.cp-gallery-slide{position:absolute;inset:0;padding:8px;box-sizing:border-box}
/* !important here is a deliberate, targeted override — App.css has a
   global "img { height: auto !important }" rule (mobile-only, meant for
   dashboard pages) that silently wins over any non-important height on
   every img in the app, including this one. Without matching its
   !important, the gallery photo reverts to its natural aspect ratio at
   height:auto and visibly overflows/crops inside this frame's
   overflow:hidden on mobile. object-fit itself isn't affected — only the
   height/width that object-fit needs to size against was being clobbered. */
.cp-gallery-img{display:block;width:100% !important;height:100% !important;object-fit:contain;object-position:center top}
.cp-gallery-arrow{position:absolute;top:50%;transform:translateY(-50%);width:34px;height:34px;border-radius:50%;background:rgba(255,255,255,.94);border:1px solid var(--cp-border);display:flex;align-items:center;justify-content:center;cursor:pointer;opacity:0;transition:opacity .2s,transform .15s,box-shadow .15s;z-index:2;color:var(--cp-text)}
.cp-gallery-arrow:hover{transform:translateY(-50%) scale(1.08);box-shadow:0 4px 12px rgba(0,0,0,.15)}
.cp-gallery-main:hover .cp-gallery-arrow{opacity:1}
.cp-gallery-arrow--left{left:16px}
.cp-gallery-arrow--right{right:16px}
.cp-gallery-counter{position:absolute;bottom:16px;right:16px;color:var(--cp-faint);font-size:11px;font-weight:500;letter-spacing:.04em}
.cp-thumbs{display:flex;gap:10px;margin-top:18px;overflow-x:auto;padding-bottom:4px}
.cp-thumb{width:52px;height:52px;overflow:hidden;border:none;cursor:pointer;flex-shrink:0;transition:opacity .15s;opacity:.4;position:relative}
.cp-thumb::after{content:'';position:absolute;left:0;right:0;bottom:-6px;height:1.5px;background:var(--cp-blue);transform:scaleX(0);transition:transform .2s}
.cp-thumb--active{opacity:1}
.cp-thumb--active::after{transform:scaleX(1)}
.cp-thumb:not(.cp-thumb--active):hover{opacity:.75}
.cp-thumb-img{width:100%;height:100%;object-fit:cover}
.cp-thumb-video{width:100%;height:100%;background:var(--cp-dark);display:flex;align-items:center;justify-content:center;color:#fff}

/* ── PRICE SECTION ── */
.cp-price-section{padding:20px 0;border-top:1px solid var(--cp-border);margin-bottom:20px}
.cp-price-row{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap}
.cp-price-main{font-size:clamp(28px,4vw,36px);font-weight:500;color:var(--cp-text);letter-spacing:-.02em}
.cp-price-currency{font-size:14px;font-weight:500;color:var(--cp-muted)}
.cp-price-original{font-size:16px;font-weight:400;color:var(--cp-faint);text-decoration:line-through}
.cp-price-note{font-size:13px;color:var(--cp-faint);margin-top:6px}
.cp-discount-badge{display:inline-flex;align-items:center;background:var(--cp-dark);color:#fff;font-size:12px;font-weight:600;letter-spacing:.01em;padding:3px 9px;border-radius:999px;margin-left:2px}

/* ── CTAs ── */
.cp-cta-stack{display:flex;flex-direction:column;align-items:flex-start;gap:12px;margin-bottom:20px}
.cp-btn-primary{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:var(--cp-dark);color:#fff;border:none;border-radius:4px;height:54px;padding:0 32px;font-family:inherit;font-size:14px;font-weight:600;letter-spacing:.02em;cursor:pointer;transition:opacity .2s,transform .2s,box-shadow .2s;white-space:nowrap;width:100%;box-shadow:0 1px 2px rgba(0,0,0,.06),0 6px 16px rgba(0,0,0,.08)}
.cp-btn-primary:hover{opacity:.9;transform:translateY(-1px);box-shadow:0 8px 20px rgba(0,0,0,.14)}
.cp-btn-primary:active{transform:translateY(0);box-shadow:none}
.cp-btn-outline{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:transparent;color:var(--cp-text);border:1px solid var(--cp-border-dk);border-radius:4px;height:54px;padding:0 32px;font-family:inherit;font-size:14px;font-weight:600;letter-spacing:.02em;cursor:pointer;transition:border-color .15s,background .15s;white-space:nowrap;width:100%}
.cp-btn-outline:hover{border-color:var(--cp-text);background:rgba(0,0,0,.03)}
.cp-btn-whatsapp{color:#1DA851;border-color:rgba(37,211,102,.4);text-decoration:none}
.cp-btn-whatsapp:hover{border-color:#25D366;background:rgba(37,211,102,.06)}
.cp-link-secondary{background:none;border:none;color:var(--cp-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;text-decoration:underline;text-underline-offset:3px;text-decoration-color:var(--cp-border-dk);transition:color .15s}
.cp-link-secondary:hover{color:var(--cp-text)}
.cp-btn-ghost{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:var(--cp-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.cp-btn-ghost:hover{color:var(--cp-text)}

/* ── BENEFITS ── */
.cp-benefits-section{padding-top:12px;margin-bottom:12px}
.cp-benefits-label{font-size:11px;font-weight:600;color:var(--cp-faint);text-transform:uppercase;letter-spacing:.1em;margin-bottom:10px}
.cp-benefits-list{display:flex;flex-direction:column;margin-bottom:10px}
.cp-benefit-row{display:flex;align-items:flex-start;gap:11px;padding:8px 0;border-bottom:1px solid var(--cp-border);transition:padding-left .15s}
.cp-benefit-row:hover{padding-left:4px}
.cp-benefit-row:first-child{border-top:1px solid var(--cp-border)}
.cp-benefit-ico{width:17px;height:17px;border-radius:50%;background:rgba(0,113,227,.1);color:var(--cp-blue);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
.cp-benefit-ico svg{width:9px;height:9px}
.cp-benefit-txt{font-size:14px;color:var(--cp-text);line-height:1.5}
.cp-link-btn{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:var(--cp-text);background:none;border:none;cursor:pointer;padding:0;transition:gap .15s}
.cp-link-btn:hover{gap:8px}

/* ── PAYMENT INFO ── */
.cp-payment-note{font-size:12px;color:var(--cp-faint);display:flex;align-items:center;gap:6px}

/* ── TRUST BAR ── */
.cp-trust-bar{display:flex;flex-wrap:wrap;gap:10px 20px;padding:14px 0;margin-bottom:0;border-top:1px solid var(--cp-border);border-bottom:1px solid var(--cp-border)}
.cp-trust-item{display:flex;align-items:center;gap:7px;font-size:12.5px;color:var(--cp-muted);white-space:nowrap}
.cp-trust-item svg{flex-shrink:0;color:var(--cp-blue)}

/* ── SECTION TITLE (shared by How it works / FAQ) ── */
.cp-section-title{font-size:clamp(22px,2.6vw,28px);font-weight:300;letter-spacing:-.02em;color:var(--cp-text);text-align:center;margin-bottom:18px}

/* ── HOW IT WORKS ── */
.cp-howitworks-section{position:relative;z-index:0;max-width:1280px;margin:0 auto;padding:28px 48px}
.cp-howitworks-section::before{content:'';position:absolute;top:0;bottom:0;left:50%;width:100vw;margin-left:-50vw;background:var(--cp-bg-tint);z-index:-1}
.cp-howitworks-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
.cp-howitworks-step{text-align:center}
.cp-howitworks-num{width:30px;height:30px;border-radius:50%;background:var(--cp-blue);color:#fff;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;margin:0 auto 10px}
.cp-howitworks-step-title{font-size:15px;font-weight:500;color:var(--cp-text);margin-bottom:5px}
.cp-howitworks-step-txt{font-size:13px;color:var(--cp-muted);line-height:1.5;max-width:30ch;margin:0 auto}
.cp-trusted-section{max-width:1280px;margin:0 auto;padding:8px 48px 0}
.cp-trusted-section .nx-clients-label{font-size:12px;font-weight:500;color:var(--cp-muted);text-align:center;margin-bottom:14px;letter-spacing:-.01em}
.cp-trusted-section .nx-customers-grid{display:flex;flex-wrap:wrap;justify-content:center;gap:20px 32px;max-width:760px;margin:0 auto;align-items:center}
.cp-trusted-section .nx-customers-logo{display:flex;justify-content:center;align-items:center;flex:0 0 96px}
.cp-trusted-section .nx-customers-logo img{filter:grayscale(1) brightness(0.35);opacity:.65}

/* ── SOCIAL PROOF BANNER ── */
/* ── FAQ ── */
.cp-faq-section{max-width:760px;margin:0 auto;padding:44px 48px}
.cp-faq-list{display:flex;flex-direction:column}
.cp-faq-item{border-bottom:1px solid var(--cp-border)}
.cp-faq-item:first-child{border-top:1px solid var(--cp-border)}
.cp-faq-question{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;background:none;border:none;text-align:left;padding:20px 2px;font-family:inherit;font-size:14.5px;font-weight:500;color:var(--cp-text);cursor:pointer;transition:padding-left .15s,color .15s}
.cp-faq-question:hover{padding-left:6px;color:var(--cp-blue)}
.cp-faq-chevron{flex-shrink:0;color:var(--cp-faint);transition:transform .2s,color .2s}
.cp-faq-item--open .cp-faq-chevron{transform:rotate(180deg);color:var(--cp-blue)}
.cp-faq-answer{margin:-6px 2px 20px;font-size:13.5px;color:var(--cp-muted);line-height:1.65;max-width:62ch}

/* ── FINAL CTA ── */
.cp-final-cta-section{position:relative;z-index:0;text-align:center;padding:36px 24px 56px;border-top:1px solid var(--cp-border);max-width:420px;margin:0 auto}
.cp-final-cta-section::before{content:'';position:absolute;top:0;bottom:0;left:50%;width:100vw;margin-left:-50vw;background:var(--cp-bg-tint);z-index:-1}
.cp-final-cta-title{font-size:22px;font-weight:300;letter-spacing:-.02em;color:var(--cp-text);margin-bottom:22px}
.cp-final-cta-btn{height:52px}

/* ── STEP INDICATOR ── */
.cp-step-indicator{display:flex;align-items:center;gap:8px}
.cp-step{width:24px;height:24px;border-radius:50%;border:1px solid var(--cp-border-dk);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;color:var(--cp-faint);transition:all .2s}
.cp-step--active{background:var(--cp-dark);border-color:var(--cp-dark);color:#fff}
.cp-step--done{background:transparent;border-color:var(--cp-text);color:var(--cp-text)}
.cp-step-line{width:20px;height:1px;background:var(--cp-border-dk)}
.cp-step-label{font-size:12px;color:var(--cp-muted);margin-left:2px}

/* ── CHECKOUT ── */
.cp-checkout-wrap{max-width:1180px;margin:0 auto;padding:64px 48px}
.cp-checkout-grid{display:grid;grid-template-columns:1fr;gap:40px}
@media(min-width:1024px){.cp-checkout-grid{grid-template-columns:3fr 2fr;gap:80px}}
.cp-checkout-title{font-size:clamp(24px,2.5vw,32px);font-weight:300;letter-spacing:-.02em;color:var(--cp-text);margin-bottom:6px}
.cp-checkout-sub{font-size:14px;color:var(--cp-muted)}
.cp-checkout-right{position:relative}
@media(min-width:1024px){.cp-checkout-right .cp-summary-card{position:sticky;top:96px}}

/* ── FORM SECTIONS ── */
.cp-form-section{padding-top:28px;border-top:1px solid var(--cp-border);margin-bottom:28px}
.cp-form-section-title{font-size:11px;font-weight:600;color:var(--cp-faint);text-transform:uppercase;letter-spacing:.1em;margin-bottom:20px}
.cp-field{display:flex;flex-direction:column;gap:6px;margin-bottom:16px}
.cp-field:last-child{margin-bottom:0}
.cp-field-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px}
.cp-field-row--3-1{grid-template-columns:2fr 1fr}
.cp-label{font-size:12px;font-weight:500;color:var(--cp-muted)}
.cp-req{color:var(--cp-blue)}
.cp-input{border:1px solid var(--cp-border-dk);border-radius:4px;padding:11px 13px;font-size:14px;color:var(--cp-text);background:#fff;outline:none;font-family:inherit;transition:border-color .15s;width:100%}
.cp-input:focus{border-color:var(--cp-text)}
.cp-select{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%236E6E73' viewBox='0 0 24 24'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 9l6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 13px center;padding-right:34px}
.cp-textarea{resize:vertical;min-height:80px}
.cp-checkbox-row{display:flex;align-items:flex-start;gap:10px;cursor:pointer}
.cp-checkbox{width:15px;height:15px;margin-top:1px;accent-color:var(--cp-dark);flex-shrink:0;cursor:pointer}
.cp-checkbox-label{font-size:13px;color:var(--cp-muted);line-height:1.55;cursor:pointer}

/* ── QUANTITY ── */
.cp-qty-row{display:flex;align-items:center;gap:10px}
.cp-qty-btn{width:34px;height:34px;border-radius:4px;border:1px solid var(--cp-border-dk);background:#fff;color:var(--cp-text);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:border-color .15s;flex-shrink:0}
.cp-qty-btn:hover:not(:disabled){border-color:var(--cp-text)}
.cp-qty-btn:disabled{opacity:.3;cursor:not-allowed}
.cp-qty-display{width:44px;text-align:center;font-size:16px;font-weight:600;color:var(--cp-text)}
.cp-qty-note{font-size:12px;color:var(--cp-faint);margin-top:10px}
.cp-free-shipping{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:500;color:var(--cp-blue);margin-left:6px}

/* ── ORDER SUMMARY ── */
.cp-summary-card{background:var(--cp-bg-w);border:1px solid var(--cp-border);border-radius:8px;padding:32px}
.cp-summary-title{font-size:11px;font-weight:600;color:var(--cp-faint);text-transform:uppercase;letter-spacing:.1em;padding-bottom:20px;border-bottom:1px solid var(--cp-border);margin-bottom:20px}
.cp-summary-rows{display:flex;flex-direction:column;gap:12px;margin-bottom:24px}
.cp-summary-row{display:flex;justify-content:space-between;align-items:center;font-size:13px;color:var(--cp-muted)}
.cp-summary-val{font-weight:500;color:var(--cp-text)}
.cp-summary-row--accent{color:var(--cp-blue)}
.cp-summary-row--border{border-top:1px solid var(--cp-border);padding-top:12px;margin-top:4px}
.cp-summary-accent-txt{color:var(--cp-blue);font-weight:500}
.cp-summary-warn-txt{color:#B45309}
.cp-summary-total{display:flex;justify-content:space-between;align-items:center;border-top:1px solid var(--cp-text);padding-top:16px;margin-top:8px;font-size:17px;font-weight:500;color:var(--cp-text)}
.cp-discount-section{padding:18px 0;border-top:1px solid var(--cp-border);margin-bottom:18px}
.cp-discount-row{display:flex;gap:8px;margin-top:8px}
.cp-btn-apply{background:var(--cp-dark);color:#fff;border:none;border-radius:4px;padding:0 16px;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;flex-shrink:0;transition:opacity .15s}
.cp-btn-apply:hover{opacity:.8}
.cp-btn-remove{background:none;color:var(--cp-blue);border:1px solid var(--cp-border-dk);border-radius:4px;padding:0 16px;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;flex-shrink:0;transition:border-color .15s}
.cp-btn-remove:hover{border-color:var(--cp-blue)}
.cp-discount-ok{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:500;color:var(--cp-blue);margin-top:10px}
.cp-terms-row{display:flex;align-items:flex-start;gap:10px;cursor:pointer;padding:18px 0;border-top:1px solid var(--cp-border);margin-bottom:18px}
.cp-terms-warning{font-size:12px;color:#B45309;text-align:center;margin-top:-10px}
.cp-btn-pay{width:100%;height:52px;border-radius:4px;background:var(--cp-dark);color:#fff;font-size:14px;font-weight:600;letter-spacing:.02em;border:none;cursor:pointer;font-family:inherit;transition:opacity .2s}
.cp-btn-pay:hover:not(:disabled){opacity:.82}
.cp-btn-pay--disabled{opacity:.4;cursor:not-allowed}
.cp-mobile-stickybar{display:none}
.cp-step1-mobile-cta{display:none}
.cp-gallery-dots{display:none}
.cp-mobile-progress-wrap{display:none}
.cp-step1-bottom-spacer{display:none}

/* ── MODALS ── */
.cp-modal-overlay{position:fixed;inset:0;z-index:200;background:rgba(17,17,17,.4);backdrop-filter:blur(2px);display:flex;align-items:center;justify-content:center;padding:20px;animation:cpFadeIn .2s ease}
.cp-modal{background:#fff;border-radius:8px;max-width:600px;width:100%;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.18);animation:cpSlideUp .25s cubic-bezier(.16,1,.3,1)}
.cp-modal--wide{max-width:720px}
.cp-modal-head{display:flex;align-items:flex-start;justify-content:space-between;padding:28px 32px;border-bottom:1px solid var(--cp-border);flex-shrink:0}
.cp-modal-h2{font-size:18px;font-weight:500;color:var(--cp-text);margin-bottom:4px;letter-spacing:-.01em}
.cp-modal-sub{font-size:13px;color:var(--cp-muted)}
.cp-modal-close{width:28px;height:28px;border-radius:50%;background:none;border:1px solid var(--cp-border-dk);cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--cp-muted);flex-shrink:0;transition:border-color .15s,color .15s}
.cp-modal-close:hover{border-color:var(--cp-text);color:var(--cp-text)}
.cp-modal-body{padding:28px 32px;overflow-y:auto;flex:1}
.cp-modal-foot{padding:20px 32px;border-top:1px solid var(--cp-border);flex-shrink:0}
.cp-terms-body{font-size:13px;line-height:1.7;color:var(--cp-muted)}

/* ── BENEFITS MODAL ── */
.cp-benefits-modal-list{display:flex;flex-direction:column}
.cp-benefit-card{display:flex;align-items:flex-start;gap:16px;padding:16px 0;border-bottom:1px solid var(--cp-border)}
.cp-benefit-card:first-child{border-top:1px solid var(--cp-border)}
.cp-benefit-card-num{font-size:12px;font-weight:500;color:var(--cp-faint);flex-shrink:0;padding-top:1px}
.cp-benefit-card-txt{font-size:14px;color:var(--cp-text);line-height:1.55}

/* ── QUOTE MODAL ── */
.cp-quote-success{text-align:center;padding:40px 0}
.cp-success-ico{width:48px;height:48px;border-radius:50%;background:var(--cp-bg);border:1px solid var(--cp-border);color:var(--cp-blue);display:flex;align-items:center;justify-content:center;margin:0 auto 16px}
.cp-quote-success h3{font-size:17px;font-weight:500;color:var(--cp-text);margin-bottom:8px}
.cp-quote-success p{font-size:14px;color:var(--cp-muted);line-height:1.6}
.cp-qty-toggle-row{display:flex;gap:16px;margin-top:8px}
.cp-toggle-opt{display:flex;align-items:center;gap:5px;font-size:12px;color:var(--cp-muted);cursor:pointer}
.cp-toggle-opt input{accent-color:var(--cp-dark)}
.cp-error{font-size:12px;color:#DC2626;margin-top:4px}
.cp-btn-submit-quote{width:100%;height:46px;border-radius:4px;background:var(--cp-dark);color:#fff;font-size:13px;font-weight:600;border:none;cursor:pointer;font-family:inherit;margin-top:10px;transition:opacity .15s}
.cp-btn-submit-quote:hover:not(:disabled){opacity:.82}
.cp-btn-submit-quote:disabled{opacity:.4;cursor:not-allowed}

/* ── LOADING / EMPTY ── */
.cp-center-screen{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;gap:14px}
.cp-spinner{width:26px;height:26px;border:1.5px solid var(--cp-border);border-top-color:var(--cp-text);border-radius:50%;animation:cpSpin .7s linear infinite}
@keyframes cpSpin{to{transform:rotate(360deg)}}
.cp-loading-txt{font-size:13px;color:var(--cp-muted)}
.cp-empty-txt{font-size:15px;color:var(--cp-muted)}

/* ── ANIMATIONS ── */
@keyframes cpFadeIn{from{opacity:0}to{opacity:1}}
@keyframes cpSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}

/* ── RESPONSIVE ── */
@media(max-width:1023px){
  .cp-product-layout{grid-template-columns:1fr;gap:40px;padding:48px 32px 64px}
  .cp-gallery-col{position:static}
  .cp-checkout-wrap{padding:40px 24px}
  .cp-field-row{grid-template-columns:1fr}
  .cp-field-row--3-1{grid-template-columns:1fr}
}
@media(max-width:640px){
  .cp-nav-inner{padding:0 20px;height:56px}
  .cp-product-layout{padding:20px 20px 40px;gap:20px}
  .cp-checkout-wrap{padding:32px 16px}
  .cp-modal-body{padding:22px 20px}
  .cp-modal-head{padding:22px 20px}
  .cp-modal-foot{padding:16px 20px}
  .cp-step-indicator{display:none}
  .cp-gallery-main{aspect-ratio:1;border-radius:14px}
  .cp-gallery-media-frame{padding:6px}
  .cp-thumbs{margin-top:10px}
  .cp-thumb{width:44px;height:44px}
  .cp-details-col{padding-top:8px}
  .cp-product-head{margin-bottom:18px}
  .cp-product-title{font-size:26px;margin-bottom:8px}
  .cp-product-subtitle{font-size:14px}
  .cp-price-section{padding:16px 0;margin-bottom:18px}
  .cp-price-main{font-size:24px}
  .cp-cta-stack{margin-bottom:24px;gap:10px}
  .cp-btn-primary{height:48px}
  .cp-btn-outline{height:48px}

  /* ── Checkout step (step 2) ── */
  .cp-checkout-title{font-size:22px}
  .cp-checkout-sub{font-size:13px}
  .cp-form-section{background:var(--cp-bg-w);border:1px solid var(--cp-border);border-radius:14px;padding:18px 16px;margin-bottom:16px;border-top:1px solid var(--cp-border)}
  .cp-form-section-title{margin-bottom:16px}
  .cp-field input,.cp-field select,.cp-input{height:46px;font-size:15px}
  .cp-field-row{gap:10px;margin-bottom:14px}
  .cp-field{margin-bottom:14px}
  .cp-summary-card{padding:18px 16px;border-radius:14px}
  .cp-summary-title{padding-bottom:14px;margin-bottom:14px}
  .cp-terms-row{padding:14px 0}
  /* The real pay button + terms checkbox stay in the flow (so the terms
     text and link to T&C/privacy are always reachable by scrolling) —
     only hide the OLD standalone button, since the sticky bar below now
     carries that same action everywhere else on the screen. */
  .cp-checkout-right>.cp-summary-card>.cp-btn-pay:not(.cp-mobile-stickybar-btn){display:none}
  .cp-checkout-wrap{padding-bottom:104px}

  .cp-mobile-stickybar{
    display:flex;align-items:center;gap:12px;
    position:fixed;left:0;right:0;bottom:0;z-index:40;
    background:var(--cp-bg-w);border-top:1px solid var(--cp-border);
    padding:12px 16px;padding-bottom:calc(12px + env(safe-area-inset-bottom,0px));
    box-shadow:0 -8px 24px rgba(0,0,0,.06);
  }
  .cp-mobile-stickybar-price{display:flex;flex-direction:column;line-height:1.2;flex-shrink:0}
  .cp-mobile-stickybar-label{font-size:11px;color:var(--cp-muted)}
  .cp-mobile-stickybar-amount{font-size:17px;font-weight:600;color:var(--cp-text);white-space:nowrap}
  .cp-mobile-stickybar-amount small{font-size:11px;font-weight:500;color:var(--cp-muted)}
  .cp-mobile-stickybar-btn{width:auto;flex:1;height:46px;font-size:13.5px}

  /* ── Gallery: swipe + dots replace hover-only arrows / small thumbs ── */
  /* Hover-reveal never works on touch, so mobile gets its own always-on
     version of the same arrows instead of relying on swipe alone — swipe
     stays as a second, faster way to browse, but isn't always obvious on
     first visit, and the dots below only show position, not "tap here to
     move". */
  .cp-gallery-arrow{opacity:1;width:32px;height:32px;box-shadow:0 2px 8px rgba(0,0,0,.12)}
  .cp-thumbs{display:none}
  .cp-gallery-dots{display:flex;justify-content:center;gap:7px;margin-top:14px}
  .cp-gallery-dot{width:7px;height:7px;border-radius:50%;border:none;padding:0;background:var(--cp-border-dk);cursor:pointer;transition:background .15s,transform .15s}
  .cp-gallery-dot--active{background:var(--cp-blue);transform:scale(1.3)}

  /* ── Step 1: mobile sticky buy bar ── */
  .cp-cta-stack{margin-bottom:0}
  .cp-step1-mobile-cta{
    display:flex;flex-direction:column;gap:8px;
    position:fixed;left:0;right:0;bottom:0;z-index:40;
    background:var(--cp-bg-w);border-top:1px solid var(--cp-border);
    padding:12px 16px;padding-bottom:calc(10px + env(safe-area-inset-bottom,0px));
    box-shadow:0 -8px 24px rgba(0,0,0,.06);
    transform:translateY(100%);opacity:0;pointer-events:none;
    transition:transform .25s ease,opacity .25s ease;
  }
  .cp-step1-mobile-cta--visible{transform:translateY(0);opacity:1;pointer-events:auto}
  .cp-step1-mobile-cta-top{display:flex;align-items:center;gap:12px}
  .cp-step1-mobile-cta-btn{height:46px;font-size:13.5px;flex:1}
  .cp-step1-mobile-trust{display:flex;align-items:center;justify-content:center;gap:6px;font-size:11px;color:var(--cp-faint)}
  .cp-step1-bottom-spacer{display:block;height:148px}

  /* ── Step 2: mobile compact progress + order recap ── */
  .cp-mobile-progress-wrap{display:block;padding:14px 16px 0}
  .cp-mobile-progress-row{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8px}
  .cp-mobile-progress-label{font-size:11px;font-weight:500;color:var(--cp-muted)}
  .cp-mobile-progress-bar{height:3px;border-radius:2px;background:var(--cp-border);overflow:hidden;margin-bottom:14px}
  .cp-mobile-progress-fill{width:100%;height:100%;background:var(--cp-blue);border-radius:2px}
  .cp-mobile-order-recap{display:flex;align-items:center;justify-content:space-between;gap:12px;background:var(--cp-bg-w);border:1px solid var(--cp-border);border-radius:12px;padding:12px 14px}
  .cp-mobile-order-recap-info{display:flex;flex-direction:column;gap:2px;min-width:0}
  .cp-mobile-order-recap-name{font-size:13px;font-weight:500;color:var(--cp-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .cp-mobile-order-recap-qty{font-size:12px;color:var(--cp-faint)}
  .cp-mobile-order-recap-total{font-size:16px;font-weight:600;color:var(--cp-text);flex-shrink:0}

  /* ── New /comprar sections: how it works, social proof, FAQ, final CTA ── */
  .cp-trust-bar{gap:10px 14px;padding:14px 0}
  .cp-trust-item{font-size:11.5px}
  .cp-section-title{margin-bottom:16px}
  .cp-howitworks-section{padding:28px 20px}
  .cp-howitworks-grid{grid-template-columns:1fr;gap:20px}
  .cp-trusted-section{padding:4px 20px 0}
  .cp-trusted-section .nx-customers-grid{gap:14px 20px}
  .cp-trusted-section .nx-customers-logo{flex:0 0 76px}
  .cp-trusted-section .nx-customers-logo img{height:20px !important}
  .cp-faq-section{padding:48px 20px}
  .cp-faq-question{font-size:14px;padding:16px 2px}
  .cp-final-cta-section{padding:48px 20px 64px}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default ComprarPage;
